import React, {HTMLAttributes, useCallback, useEffect, useState} from "react";
import {CommentsModal} from "./CommentsModal";
import {Channel} from "./Channel/Channel";
import {ExternalUserProvider} from "../ExternalUserProvider";
import useChannelPreview from "../api/useChannelPreview";
import CommentRow from "./CommentRow";
import {Trans, useTranslation} from "react-i18next";
import {Comment} from "../api/Models";
import useChannelSettings from "../api/useChannelSettings";

import {Info, MessageSquare, Send, X} from "react-feather";
import { TrackingFunction } from "./TrackingFunction";

export type CommentsDisplayMode = 'modal' | 'inline'

// Initial page size for the expanded comment thread, per display mode. Modal is a
// scrollable overlay so it loads more up front; inline expands within the page flow.
const MODAL_COMMENT_LIMIT = 50
const INLINE_COMMENT_LIMIT = 10

export interface ChannelPreviewProps {
  ownerId: string,
  channelId: string,
  version?: number,
  position?: number,
  externalUserProvider?: ExternalUserProvider,
  portalClassName?: string,
  portalStyle?: object
  onCommentManage?: (commentId: string) => void
  useCookies?: boolean
  trackingFunction?: TrackingFunction
  displayMode?: CommentsDisplayMode
}

interface CommentsContainerProps extends HTMLAttributes<HTMLDivElement>{
  position?: number
}

export function CommentsContainer({position, children, ...attributes} : CommentsContainerProps) {
  return <div className={`tik4-content-block tik4-content-block--comments ${position ? `tik4-content-block--position-${position}`: ''}`} {...attributes}>
    <div className="tik4-comments">
      {children}
    </div>
  </div>
}

function useModal(channelId: string): [boolean, (val: boolean) => void] {
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    const eventHandler = (event: any)  => {
      if(event.detail && event.detail === channelId){
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    }
    document.addEventListener("tikShowCommentChannelModal", eventHandler);
    return () => document.removeEventListener("tikShowCommentChannelModal", eventHandler);
  }, [channelId]);

  function setShowUniqueModal(show: boolean) {
    if(show){
      document.dispatchEvent(new CustomEvent('tikShowCommentChannelModal', {detail: channelId}));
    }
    else {
      setShowModal(false);
    }
  }

  return [showModal, setShowUniqueModal]
}

export default function ChannelPreview({ownerId, channelId, version, position, externalUserProvider, onCommentManage, portalClassName, portalStyle, useCookies, trackingFunction, displayMode = 'modal'}: ChannelPreviewProps){
  const {isLoading, total, data, hasNextPage, error, refetch} = useChannelPreview({ownerId, channelId, version});
  const {data: settings}  = useChannelSettings({ownerId, channelId, version: (version || 0)});
  const [showModal, setShowModal] = useModal(channelId);
  const {t} = useTranslation("liveblog");

  const isInline = displayMode === 'inline'
  const commentLimit = isInline ? INLINE_COMMENT_LIMIT : MODAL_COMMENT_LIMIT

  const portalClassNamesOrDefault = portalClassName || 'tik4 tik4--on-light'
  const portalStyleOrDefault = portalStyle || {}

  const hasPostButton = (settings && settings?.publishMode !== "ReadOnly")

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, [setShowModal])

  const handleModalOpen = useCallback(() => {
    trackingFunction && trackingFunction({t: "c_opn"})
    setShowModal(true)
  }, [setShowModal, trackingFunction])

  const handleCommentCreated = useCallback((comment: Comment) => {
     if(comment.publishedAt){
       refetch();
     }
  }, [refetch])

  if(isLoading){
    return <CommentsContainer position={position}>
      <div className="tik4-comments__loader">
        {t('liveblog.comments.preview.loading')}
      </div>
    </CommentsContainer>;
  }
  if(error){
    // TODO improve messages
    throw Error("Could not load comments " + error);
  }

  function renderWriteComment(){
    if(!hasPostButton){
      return null;
    }
    let btnText;
    if(externalUserProvider && externalUserProvider.getCurrentUser()){
      btnText = t('liveblog.comments.button.comment.as', {username: externalUserProvider.getCurrentUser()!.name});
    } else {
      btnText = t('liveblog.comments.button.comment.generic');
    }
    const needsLoginWithoutTrigger = !settings?.allowAnonymousPost && externalUserProvider && !externalUserProvider.getCurrentUser() && !externalUserProvider.triggerLogin
    //
    return <div className="tik4-comments__write">
      {/* TODO:Matthias Trigger login as well */}
      <button className="tik4-form-field tik4-form-field--sm tik4-comments__write__button" onClick={handleModalOpen} disabled={needsLoginWithoutTrigger} type="button">
        <span className="tik4-comments__write__button__placeholder">{btnText}</span>
        <Send className="tik4-icon tik4-comments__write__button__icon" />
      </button>

      {!settings?.allowAnonymousPost && externalUserProvider && !externalUserProvider.getCurrentUser() && <div className="tik4-hint tik4-hint--sm tik4-comments__write__hint">
        <Info className="tik4-icon" />
        <div className="tik4-hint__content">
          {externalUserProvider.triggerLogin ?
            <Trans i18nKey="liveblog.comments.login.with_button">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              Please <a href="#" className="tik4-link tik4-link--within-body" onClick={externalUserProvider.triggerLogin}>log in</a> to comment this message.
            </Trans>
            :
            t('liveblog.comments.login.no_button')
          }
        </div>

      </div>}

    </div>
  }

  const previewHeader = <div className="tik4-comments__header">
    <div className="tik4-comments__header__label">
      <MessageSquare className="tik4-comments__header__label__icon"/>
      <span className="tik4-comments__header__label__content">
        {t('liveblog.comments.preview.headline', {count: total})}
      </span>
    </div>
    {isInline && showModal && <button className="tik4-comments__header__close" onClick={handleModalClose} type="button">
      <X className="tik4-icon tik4-comments__header__close__icon" />
    </button>}
  </div>

  const previewBody = <>
    {renderWriteComment()}

    <div className="tik4-comments__list">
      {data.map((comment) =>
        <CommentRow key={comment.id} comment={comment} onCommentManage={onCommentManage}/>
      )}
    </div>
    {hasNextPage && <div className="tik4-more-comments">
      <button className="tik4-more-comments__button" onClick={handleModalOpen} type="button">
        {isInline ? t('liveblog.comments.modal.show_more_comments') : t('liveblog.comments.preview.show-all-comments', {count: total})}</button>
    </div>}
  </>

  const channel = <Channel
    ownerId={ownerId}
    channelId={channelId}
    externalUserProvider={externalUserProvider}
    onClose={handleModalClose}
    onCommentCreated={handleCommentCreated}
    onCommentManage={onCommentManage}
    version={version}
    useCookies={useCookies}
    trackingFunction={trackingFunction}
    limit={commentLimit}
    displayMode={displayMode}
  />

  return <CommentsContainer position={position}>
    {/* The header stays mounted across collapse/expand in inline mode so it never swaps out for the modal's header; modal keeps the whole preview (header + body) visible behind the overlay. */}
    {previewHeader}
    {(!isInline || !showModal) && previewBody}
    {isInline
      ? (showModal && channel)
      : <CommentsModal
          isOpen={showModal}
          onRequestClose={handleModalClose}
          className={portalClassNamesOrDefault}
          style={{content: portalStyleOrDefault}}
        >
        {channel}
      </CommentsModal>}
  </CommentsContainer>
}